import { useCallback, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Check, Instagram, Phone } from "lucide-react";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SidebarMenuSubButton, SidebarMenuSubItem } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

type ConversationChannel = "whatsapp" | "instagram" | "messenger";

function ChannelIcon({ channel, className }: { channel: ConversationChannel; className?: string }) {
  if (channel === "whatsapp") return <Phone className={className} />;
  if (channel === "instagram") return <Instagram className={className} />;
  return <MessengerIcon className={className} />;
}

function channelIconWrapClass(channel: ConversationChannel) {
  return cn(
    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5",
    channel === "whatsapp" && "bg-green-500/10 text-green-600",
    channel === "instagram" && "bg-pink-500/10 text-pink-600",
    channel === "messenger" && "bg-blue-500/10 text-blue-600",
  );
}

function lineOverflow(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

function labelsOverflow(root: HTMLElement | null): boolean {
  if (!root) return false;
  if (lineOverflow(root)) return true;
  const lines = root.querySelectorAll<HTMLElement>("[data-sidebar-label-line]");
  return Array.from(lines).some(lineOverflow);
}

function scheduleOverflowCheck(root: HTMLElement | null, onResult: (overflow: boolean) => void) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => onResult(labelsOverflow(root)));
  });
}

type IntegrationLinkProps = {
  channel: ConversationChannel;
  integrationId: string;
  name: string;
  detail?: string | null;
  isSelected: boolean;
  unreadCount: number;
};

export function ConversationSidebarIntegrationLink({
  channel,
  integrationId,
  name,
  detail,
  isSelected,
  unreadCount,
}: IntegrationLinkProps) {
  const labelsRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const closeTooltip = useCallback(() => setTooltipOpen(false), []);

  const openTooltipIfUseful = useCallback(() => {
    scheduleOverflowCheck(labelsRef.current, (overflow) => {
      if (overflow || name.length > 16 || (detail?.length ?? 0) > 20) {
        setTooltipOpen(true);
      }
    });
  }, [name, detail]);

  return (
    <SidebarMenuSubItem>
      <Tooltip open={tooltipOpen} onOpenChange={(wantOpen) => !wantOpen && closeTooltip()}>
        <TooltipTrigger asChild>
          <SidebarMenuSubButton
            asChild
            isActive={isSelected}
            size="sm"
            className="h-auto min-h-0 py-0.5 px-1.5 leading-tight"
          >
            <NavLink
              to={`/conversations?channel=${channel}&integration_id=${integrationId}`}
              className="gap-1.5 items-start"
              onPointerEnter={openTooltipIfUseful}
              onPointerLeave={closeTooltip}
              onFocus={openTooltipIfUseful}
              onBlur={closeTooltip}
            >
              <span className={channelIconWrapClass(channel)}>
                {isSelected ? <Check className="h-3 w-3" /> : <ChannelIcon channel={channel} className="h-3 w-3" />}
              </span>
              <span ref={labelsRef} className="min-w-0 flex-1 overflow-hidden">
                <span
                  data-sidebar-label-line
                  className="block min-w-0 w-full truncate text-[11px] font-medium leading-snug"
                >
                  {name}
                </span>
                {detail ? (
                  <span
                    data-sidebar-label-line
                    className="block min-w-0 w-full truncate text-[9px] text-muted-foreground leading-snug mt-px"
                  >
                    {detail}
                  </span>
                ) : null}
              </span>
              {unreadCount > 0 && (
                <span className="ml-auto shrink-0 rounded-full bg-green-500 px-1 py-px text-[9px] font-semibold leading-none text-white tabular-nums">
                  {unreadCount}
                </span>
              )}
            </NavLink>
          </SidebarMenuSubButton>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={12} className="max-w-[min(280px,80vw)]">
          <p className="text-xs font-medium leading-snug">{name}</p>
          {detail ? <p className="text-xs text-muted-foreground leading-snug mt-0.5">{detail}</p> : null}
        </TooltipContent>
      </Tooltip>
    </SidebarMenuSubItem>
  );
}

type OrphanChannelLinkProps = {
  channel: ConversationChannel;
  isSelected: boolean;
  unreadCount: number;
};

export function ConversationSidebarOrphanChannelLink({
  channel,
  isSelected,
  unreadCount,
}: OrphanChannelLinkProps) {
  const labelsRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const title = "Outras no canal";
  const detail = "Sem integração ou mistas";

  const closeTooltip = useCallback(() => setTooltipOpen(false), []);

  const tryOpenTooltip = useCallback(() => {
    scheduleOverflowCheck(labelsRef.current, (overflow) => {
      if (overflow) setTooltipOpen(true);
    });
  }, []);

  return (
    <SidebarMenuSubItem>
      <Tooltip open={tooltipOpen} onOpenChange={(wantOpen) => !wantOpen && closeTooltip()}>
        <TooltipTrigger asChild>
          <SidebarMenuSubButton
            asChild
            isActive={isSelected}
            size="sm"
            className="h-auto min-h-0 py-0.5 px-1.5 leading-tight"
          >
            <NavLink
              to={`/conversations?channel=${channel}`}
              className="gap-1.5 items-start"
              onPointerEnter={tryOpenTooltip}
              onPointerLeave={closeTooltip}
              onFocus={tryOpenTooltip}
              onBlur={closeTooltip}
            >
              <span className={channelIconWrapClass(channel)}>
                {isSelected ? <Check className="h-3 w-3" /> : <ChannelIcon channel={channel} className="h-3 w-3" />}
              </span>
              <span ref={labelsRef} className="min-w-0 flex-1 overflow-hidden">
                <span
                  data-sidebar-label-line
                  className="block min-w-0 w-full truncate text-[11px] font-medium leading-snug"
                >
                  {title}
                </span>
                <span
                  data-sidebar-label-line
                  className="block min-w-0 w-full truncate text-[9px] text-muted-foreground leading-snug mt-px"
                >
                  {detail}
                </span>
              </span>
              <span className="ml-auto shrink-0 rounded-full bg-green-500 px-1 py-px text-[9px] font-semibold leading-none text-white tabular-nums">
                {unreadCount}
              </span>
            </NavLink>
          </SidebarMenuSubButton>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={12} className="max-w-[min(280px,80vw)]">
          <p className="text-xs font-medium leading-snug">{title}</p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5">{detail}</p>
        </TooltipContent>
      </Tooltip>
    </SidebarMenuSubItem>
  );
}
